import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { DatabaseBackup, Download, RefreshCw, Clock, HardDrive } from "lucide-react";
import { EmailPanel } from "@/components/EmailPanel";

const fmtSize = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

export default function Backups() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/backup/list").then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      toast.success(`${t("backup_done")} · ${fmtSize(data.size)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const download = async (rec) => {
    try {
      const res = await api.get(`/files/${rec.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = rec.original_filename || "backup.gz";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const downloadDirect = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meoscargo-backup-${new Date().toISOString().slice(0, 10)}.json.gz`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("backup_done"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const emailNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/email");
      toast.success(`${t("backup_email_sent")}: ${data.recipient}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="backups-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <DatabaseBackup className="w-5 h-5 text-emerald-600" /> {t("backups")}
          </h2>
          <p className="text-sm text-slate-500">{rows.length} {t("backups").toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="backup-refresh" onClick={load} className="border border-slate-300 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> {t("refresh")}
          </button>
          <button data-testid="backup-email-now" disabled={busy} onClick={emailNow} className="border border-indigo-600 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> {t("email_backup_now")}
          </button>
          <button data-testid="backup-download-direct" disabled={busy} onClick={downloadDirect} className="border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> {t("download_backup_pc")}
          </button>
          <button data-testid="run-backup-btn" disabled={busy} onClick={run} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <DatabaseBackup className="w-4 h-4" /> {busy ? "…" : t("run_backup")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400"><Clock className="w-3.5 h-3.5" /> {t("backup_schedule")}</div>
          <p className="mt-1.5 text-sm text-slate-700">{t("backup_schedule_value")}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400"><HardDrive className="w-3.5 h-3.5" /> {t("backup_where")}</div>
          <p className="mt-1.5 text-sm text-slate-700">{t("backup_where_value")}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400"><DatabaseBackup className="w-3.5 h-3.5" /> {t("backup_restore")}</div>
          <p className="mt-1.5 text-xs font-mono text-slate-600 break-all">mongorestore --gzip --archive=file.gz</p>
        </div>
      </div>

      <EmailPanel />

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="p-3 text-start">{t("date")}</th>
              <th className="p-3 text-start">{t("file")}</th>
              <th className="p-3 text-end">{t("size")}</th>
              <th className="p-3 text-start">{t("who")}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400">{t("no_backups")}</td></tr>}
            {rows.map(r => (
              <tr key={r.id} data-testid={`backup-row-${r.id}`}>
                <td className="p-3 tabular-nums text-slate-600">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-3 font-mono text-xs">{r.original_filename}</td>
                <td className="p-3 text-end tabular-nums">{fmtSize(r.size)}</td>
                <td className="p-3 text-xs text-slate-500">{r.created_by_name || (r.created_by === "system" ? "cron" : "—")}</td>
                <td className="p-3 text-end">
                  <button data-testid={`backup-dl-${r.id}`} onClick={() => download(r)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-emerald-600 hover:text-emerald-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold">
                    <Download className="w-3.5 h-3.5" /> {t("download")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
